import {Injectable} from '@angular/core';
import {Subject, of} from 'rxjs';
import {Observable} from 'rxjs/internal/Observable';
import {catchError, map, tap} from 'rxjs/operators';
import {LimitExceededResponse, SubscriptionStatusDto} from '@core/models/payment.model';
import {PaymentFacadeService} from '@core/services/entities/payment/payment-facade.service';

/**
 * Шина событий paywall — HTTP-интерсептор бросает в неё 402-ответы,
 * PaywallHostComponent слушает и открывает модалку. Компоненты сами 402 не ловят.
 */
@Injectable({providedIn: 'root'})
export class PaywallService {
  private readonly _open$ = new Subject<LimitExceededResponse>();

  readonly open$: Observable<LimitExceededResponse> = this._open$.asObservable();

  constructor(private facade: PaymentFacadeService) {}

  trigger(payload: LimitExceededResponse): void {
    this._open$.next(payload);
  }

  /**
   * Проверяет лимит мест до открытия формы приглашения.
   * Если лимит исчерпан — автоматически открывает paywall и возвращает true.
   * При ошибке запроса возвращает false — API вернёт 402 сам по себе.
   */
  checkSeats(companyId: string): Observable<boolean> {
    return this.facade.subscriptionStatus(companyId).pipe(
      tap(status => {
        if (status.seats.available <= 0) {
          this.trigger(this._buildPayload(status));
        }
      }),
      map(status => status.seats.available <= 0),
      catchError(() => of(false)),
    );
  }

  private _buildPayload(status: SubscriptionStatusDto): LimitExceededResponse {
    return {
      error: 'limit_exceeded',
      action: 'add_user',
      limit: status.seats.purchased,
      used: status.seats.used,
      tariff: status.tariff ?? {id: '', title: 'Free', isFree: true, pricePerUserMonthly: 0},
      upsell: {
        type: (!status.tariff || status.tariff.isFree) ? 'upgrade' : 'expansion',
        suggestedAddUsers: 1,
        paymentMethodKnown: false,
      },
    };
  }
}
